import { useGet, usePost, usePut, useDelete } from 'lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { apiViews } from 'api/views';
import type { Response } from 'types';
import type { View, ViewListResponse, ViewReorderCommand, ViewUpdateCommand } from 'types/views';

// Helper function to transform view response data
export const transformViewResponse = (data: Response<View> | View): View => {
  if ('data' in data && data.data) {
    return data.data;
  }
  return data as View;
};

export const useViews = (entityType: string) => {
  return useGet<ViewListResponse>(
    apiViews.get.list,
    ['views', entityType],
    [entityType],
    (data: Response<{ views: View[] }> | { views: View[] }) => {
      if ('data' in data && data.data?.views) {
        return { views: data.data.views };
      } else if ('views' in data && data.views) {
        return { views: data.views };
      }
      throw new Error('Invalid API response structure');
    }
  );
};

export const useView = (entityType: string, viewId: number) => {
  return useGet<View>(
    apiViews.get.byId,
    ['views', entityType, viewId.toString()],
    [entityType, viewId.toString()],
    transformViewResponse,
    true,
    { enabled: !!viewId }
  );
};

export const useFetchView = (entityType: string) => {
  const queryClient = useQueryClient();

  const fetchView = async (viewId: number): Promise<View> => {
    const fullViewData = await queryClient.fetchQuery({
      queryKey: ['views', entityType, viewId.toString()],
      queryFn: async () => {
        const response = await apiViews.get.byId({ vars: [entityType, viewId.toString()] });
        return transformViewResponse(response.data as View);
      },
    });

    return fullViewData;
  };

  return { fetchView };
};

export const useCreateView = (entityType: string) => {
  const queryClient = useQueryClient();
  return usePost<View>(apiViews.post.create, false, [entityType], {
    // Stable key so callers can observe create-view results via
    // `useMutationState({ filters: { mutationKey: ['createView', entityType] } })`
    // without piping a callback through every shared component layer.
    mutationKey: ['createView', entityType],
    onSuccess: () => {
      // Invalidate views list to refetch with new view
      queryClient.invalidateQueries({ queryKey: ['views', entityType] });
    },
  });
};

export const useUpdateView = (entityType: string) => {
  const queryClient = useQueryClient();
  return usePut<ViewUpdateCommand>(apiViews.put.update, false, {
    onSuccess: () => {
      // Invalidate all views queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['views', entityType] });
    },
  });
};

export const useDeleteView = (entityType: string) => {
  const queryClient = useQueryClient();
  return useDelete<View>(apiViews.delete.byId, false, [entityType], {
    onSuccess: () => {
      // Invalidate views list to refetch without deleted view
      queryClient.invalidateQueries({ queryKey: ['views', entityType] });
    },
  });
};

export const useReorderViews = (entityType: string) => {
  const queryClient = useQueryClient();
  return usePut<ViewReorderCommand>(apiViews.put.reorder, false, {
    onSuccess: () => {
      // Invalidate views list to refetch with new order
      queryClient.invalidateQueries({ queryKey: ['views', entityType] });
    },
  });
};
